<template>
  <div class="sales-step-content">
    <div class="text-center mb-3">
      <font-awesome-icon icon="credit-card" size="3x" class="text-success mb-2" />
      <h4>Zahlung & Bestätigung</h4>
      <p class="text-muted mb-0">
        Bestätige deine Auswahl und wähle die Zahlungsmethode
      </p>
    </div>

    <!-- Order Summary -->
    <div class="card mb-3">
      <div class="card-header py-2">
        <h6 class="mb-0">Bestellübersicht</h6>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <div class="col-12">
            <h6>Gewähltes Paket:</h6>
            <div class="d-flex align-items-center">
              <font-awesome-icon icon="coins" class="me-2 text-warning" />
              <span class="fw-bold">
                {{ selectedPackage?.coins }} Quittungen
              </span>
              <span v-if="selectedPackage?.isCustom" class="badge bg-info ms-2">Individuell</span>
            </div>
            <div class="mt-2">
              <span class="badge bg-secondary">
                {{ formatCurrency(selectedPackage?.pricePerCoin) }} pro Quittung
              </span>
            </div>
          </div>
        </div>

        <div v-if="couponInfo.applied" class="mt-3">
          <h6>Gutschein:</h6>
          <div class="d-flex flex-column flex-md-row gap-2 align-items-start align-items-md-center">
            <span class="badge bg-success text-uppercase">{{ couponInfo.code }}</span>
            <div>
              <div>{{ couponInfo.description }}</div>
              <small v-if="couponInfo.meta" class="text-muted">{{ couponInfo.meta }}</small>
            </div>
          </div>
        </div>

        <hr>
        <div class="row">
          <div class="col-6">
            <strong>Gesamtbetrag:</strong>
          </div>
          <div class="col-6 text-end">
            <div class="h4 mb-0 text-primary">
              {{ formatCurrency(priceData.totalPrice) }}
            </div>
            <small class="text-muted">inkl. MwSt.</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Billing Data Summary -->
    <div v-if="hasFilledBillingData" class="card mb-3">
      <div class="card-header py-2">
        <h6 class="mb-0">Rechnungsdaten</h6>
      </div>
      <div class="card-body p-3">
        <div class="row">
          <div
            class="col-md-6"
            v-if="fullName || billingData.company || streetLine || cityLine || billingData.country"
          >
            <h6>Rechnungsadresse:</h6>
            <ul class="list-unstyled">
              <li>
                <div v-if="fullName"><strong>{{ fullName }}</strong></div>
                <div v-if="billingData.company">{{ billingData.company }}</div>
                <div v-if="streetLine">{{ streetLine }}</div>
                <div v-if="cityLine">{{ cityLine }}</div>
                <div v-if="billingData.country">{{ billingData.country }}</div>
              </li>
            </ul>
          </div>
          <div
            class="col-md-6"
            v-if="billingData.email || billingData.taxId || billingData.vatId"
          >
            <h6>Kontaktdaten:</h6>
            <ul class="list-unstyled">
              <li>
                <div v-if="billingData.email"><strong>E-Mail:</strong> {{ billingData.email }}</div>
                <div v-if="billingData.taxId"><strong>Steuernummer:</strong> {{ billingData.taxId }}</div>
                <div v-if="billingData.vatId"><strong>USt-IdNr.:</strong> {{ billingData.vatId }}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Methods -->
    <PaymentMethods
      :price-data="priceData"
      @payment-method-changed="onPaymentMethodChanged"
    />

    <!-- Terms and Conditions -->
    <TermsAndConditions @terms-changed="onTermsChanged" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePricing } from '../composables/usePricing'
import PaymentMethods from '../components/PaymentMethods.vue'
import TermsAndConditions from '../components/TermsAndConditions.vue'

const props = defineProps({
  selectedPackage: {
    type: Object,
    default: null
  },
  priceData: {
    type: Object,
    required: true
  },
  billingData: {
    type: Object,
    required: true
  },
  hasFilledBillingData: {
    type: Boolean,
    default: false
  },
  couponResult: {
    type: Object,
    default: null
  },
  couponCode: {
    type: [String, Object],
    default: ''
  },
  isFreeUpgrade: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['payment-method-changed', 'terms-changed'])

const { formatCurrency } = usePricing()

const normalizedCouponCode = computed(() => {
  if (props.couponCode && typeof props.couponCode === 'string') {
    return props.couponCode.trim().toUpperCase()
  }
  return ''
})

const couponInfo = computed(() => {
  if (!props.couponResult?.success || !props.couponResult.data?.coupon) {
    return { applied: false }
  }

  const coupon = props.couponResult.data.coupon
  const code = normalizedCouponCode.value || coupon.code || ''

  let description = coupon.description || 'Gutschein angewendet'
  let meta = null

  if (coupon.type === 'free_coins') {
    const amount = coupon.coins || coupon.value || 0
    description = `Kostenlose ${amount} Quittungen`
    meta = amount > 0 ? `Die ersten ${amount} Quittungen sind kostenlos.` : null
  } else if (coupon.type === 'user_related') {
    description = coupon.description || 'Benutzerspezifischer Gutschein'
  } else if (coupon.type === 'free_team_pro') {
    const planText = coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro'
    description = `Kostenloses Team ${planText} Upgrade`
  }

  if (props.isFreeUpgrade && props.priceData.totalPrice === 0) {
    meta = 'Der Gesamtbetrag ist durch den Gutschein abgedeckt.'
  } else if (props.priceData.totalPrice > 0 && props.couponResult.data?.discountAmount) {
    meta = `Rabatt: ${formatCurrency(props.couponResult.data.discountAmount)}`
  }

  return {
    applied: true,
    code,
    description,
    meta
  }
})

const fullName = computed(() => {
  const first = props.billingData.firstName?.trim()
  const last = props.billingData.lastName?.trim()
  if (!first && !last) return ''
  return [first, last].filter(Boolean).join(' ')
})

const streetLine = computed(() => {
  const street = props.billingData.street?.trim()
  const number = props.billingData.number?.trim()
  if (!street && !number) return ''
  return [street, number].filter(Boolean).join(' ')
})

const cityLine = computed(() => {
  const zip = props.billingData.zip?.trim()
  const city = props.billingData.city?.trim()
  if (!zip && !city) return ''
  return [zip, city].filter(Boolean).join(' ')
})

const onPaymentMethodChanged = (method) => {
  emit('payment-method-changed', method)
}

const onTermsChanged = (accepted) => {
  emit('terms-changed', accepted)
}
</script>
